import {useMessage} from '@coolcolduk/ui-mui';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Button,
  ListItem,
  ListItemButton,
  ListItemText,
  Stack,
  Typography,
} from '@mui/material';
import {ExpandMore} from '@mui/icons-material';
import {useState} from 'react';
import {QueueJobContent} from '../../../__generated__/resolvers-types';
import {JobListButton} from '../../../components/jobList/JobListButton';
import useDeleteEtsyBulkUpdateGoogleSheetLink from './useDeleteEtsyBulkUpdateGoogleSheetLink';
import useListEtsyBulkUpdateGoogleSheetLinks from './useListEtsyBulkUpdateGoogleSheetLinks';
import useExportEtsyListingsToGoogleSheet from './useExportEtsyListingsToGoogleSheet';
import useImportGoogleSheetToEtsyListings from './useImportGoogleSheetToEtsyListings';
import EtsyBulkUpdateGoogleSheetAddLinkForm from './EtsyBulkUpdateGoogleSheetAddLinkForm';
import {FeatureProps} from '../../../types/FeatureObject';

export interface EtsyBulkUpdateGoogleSheetProps extends FeatureProps {}

export default function EtsyBulkUpdateGoogleSheet({selectedAccount}: EtsyBulkUpdateGoogleSheetProps) {
  const {showMessage} = useMessage();
  const [selectedLinkId, setSelectedLinkId] = useState<string | null>(null);

  const {links, loading: linksLoading} = useListEtsyBulkUpdateGoogleSheetLinks();
  const [deleteLink, {loading: deleteLoading}] = useDeleteEtsyBulkUpdateGoogleSheetLink({
    onCompleted: () => {
      showMessage('Link removed');
      if (selectedLinkId) setSelectedLinkId(null);
    },
    onError: (e) => showMessage(e.message, undefined, 'error'),
  });
  const [exportToSheet, {loading: exportLoading}] = useExportEtsyListingsToGoogleSheet();
  const [importFromSheet, {loading: importLoading}] = useImportGoogleSheetToEtsyListings();

  const selectedLink = selectedLinkId ? links.find((l) => l.id === selectedLinkId) : undefined;

  const handleExport = async () => {
    if (!selectedLinkId) {
      showMessage('Select a link first', undefined, 'warning');
      return;
    }
    try {
      const result = await exportToSheet(selectedLinkId);
      showMessage(`Exported ${result.count} listings to Google Sheet`, undefined, 'success');
    } catch (e) {
      showMessage(e instanceof Error ? e.message : 'Export failed', undefined, 'error');
    }
  };

  const handleImport = async () => {
    if (!selectedLinkId) {
      showMessage('Select a link first', undefined, 'warning');
      return;
    }
    try {
      const result = await importFromSheet(selectedLinkId);
      showMessage(`Import started: ${result.itemCount} listings. Check job list for progress.`, undefined, 'success');
    } catch (e) {
      showMessage(e instanceof Error ? e.message : 'Import failed', undefined, 'error');
    }
  };

  return (
    <Stack direction="column" spacing={3}>
      <JobListButton content={QueueJobContent.etsyBulkUpdateCsv} />
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMore />}>Add link</AccordionSummary>
        <AccordionDetails>
          <EtsyBulkUpdateGoogleSheetAddLinkForm selectedAccount={selectedAccount} />
        </AccordionDetails>
      </Accordion>
      <Typography variant="subtitle1">Your links</Typography>
      {linksLoading ? (
        <Typography color="text.secondary">Loading…</Typography>
      ) : links.length === 0 ? (
        <Typography color="text.secondary">No links yet. Add one above.</Typography>
      ) : (
        <>
          <Stack component="ul" spacing={0} sx={{listStyle: 'none', pl: 0, m: 0}}>
            {links.map((link) => (
              <ListItem key={link.id} disablePadding>
                <ListItemButton
                  selected={selectedLinkId === link.id}
                  onClick={() => setSelectedLinkId(selectedLinkId === link.id ? null : link.id)}
                >
                  <ListItemText
                    primary={link.name || link.spreadsheetId}
                    secondary={`${link.listingState} · ${link.etsyAccountId}`}
                  />
                </ListItemButton>
              </ListItem>
            ))}
          </Stack>
          {selectedLink && (
            <Stack direction="row" spacing={2} alignItems="center">
              <Button variant="contained" onClick={handleExport} disabled={exportLoading}>
                Etsy to Google Sheet
              </Button>
              <Button variant="contained" color="secondary" onClick={handleImport} disabled={importLoading}>
                Google Sheet to Etsy
              </Button>
              <Button
                variant="outlined"
                color="error"
                onClick={() => selectedLink && deleteLink(selectedLink.id)}
                disabled={deleteLoading}
              >
                Remove link
              </Button>
            </Stack>
          )}
        </>
      )}
    </Stack>
  );
}
